import { lightColors as colors } from "@common/consts/colors";
import { DEFAULT_LEX_IDX_OFFSET, INIT_LEX_IDX, createTodo } from "@common/utils/todo-utils";
import type { PopoverContentProps, StepType } from "@reactour/tour";
import { TourProvider, useTour } from "@reactour/tour";
import TodayIcon from "@web/public/to-day.svg";
import OnboardIllustration from "@web/public/onboarding.svg";
import type { ButtonProps } from "@web/src/app/components/button";
import { Button } from "@web/src/app/components/button";
import { useCtxHotkeys, useHotkeyContext } from "@web/src/app/hooks/useCtxHotkeys";
import { useStores } from "@web/src/hooks/useStore";
import type { Stores } from "@web/src/store/web-stores";
import classNames from "classnames";
import type { FC } from "react";
import { useEffect } from "react";

import type { GroupKey, Todo, TodoId } from "@common/types/todo";
import { Hotkey } from "@web/src/app/components/hotkeys/hotkey";
import { ConfettiEvents } from "@web/src/app/hooks/useConfetti";
import { useTutorial } from "@web/src/app/use-cases/onboarding/useTutorial";
import { WebEvents } from "@web/src/app/web-events";
import { Shortcuts } from "@web/src/consts/shortcuts";
import EventEmitter from "eventemitter3";
import * as lexi from "lexicographic-integer";
import { observer } from "mobx-react-lite";
import moment from "moment";
import { twMerge } from "tailwind-merge";
import { analytics } from "@web/src/app/use-cases/analytics";
import { PiTelegramLogoFill } from "react-icons/pi";
import { mockHomeSpace, mockWorkSpace } from "@web/src/app/use-cases/onboarding/mock-stores";
import Callout from "@web/src/app/components/callout";
import { todoIdToHighlightId } from "@web/src/app/use-cases/todo-highlight";
import { findTodoHighlightable } from "@web/src/app/use-cases/todo/find-todo-highlightable";
import { waitForSelector } from "@web/src/app/utils/dom";

export type TourEventTypes = {
  NEXT_STEP: () => void;
  PREVIOUS_STEP: () => void;
  START: () => void;
  SKIP: () => void;
  FINISH: () => void;
  ON_REGISTER_SEEN: () => void;
};

export const TourEvents = new EventEmitter<TourEventTypes>();

const SETUP_DELAY = 0;
const DEBUG_START_STEP = 0;
//========================================================================
//
//                            ROOT TOUR STEP
//
//========================================================================
interface TourStepProps {
  popoverProps: PopoverContentProps;
  totalStepCount: number;
  children: React.ReactNode;
}
const TourStep: FC<TourStepProps> = ({ children, popoverProps, totalStepCount }) => {
  const stepPercentage = popoverProps.currentStep / (totalStepCount - 1);
  return (
    <div className="text-glyph-prm">
      {Boolean(stepPercentage) && (
        <div className="bg-surface-scd w-full rounded-full h-[6px] relative mb-2 overflow-hidden">
          <div className="bg-glyph-prm absolute h-full transition-200" style={{ width: `${stepPercentage * 100}%` }} />
        </div>
      )}
      {children}
    </div>
  );
};

type TourActionProps = {
  title: string;
  shortcut?: string;
  isClickable?: boolean;
  onTrigger?: (stores: Stores) => void;
} & Partial<ButtonProps>;

type CustomTourStep = StepType & {
  nextAction?: TourActionProps;
  stateSetup?: (stores: Stores) => Promise<void>;
  stateTeardown?: (stores: Stores) => Promise<void>;
  checkReadyState?: (stores: Stores) => void;
  allowShortcuts?: string[];
};

//========================================================================
//
//                            TOUR STEPS
//
//========================================================================

function withTourState(stores: Stores, partialTodos: (Partial<Todo> & Pick<Todo, "id">)[]) {
  const todos = partialTodos.map((t) => ({
    date: moment().startOf("day").valueOf(),
    ...t,
    owner: stores.userStore.userId,
    space: stores.spaceStore.currentSpaceId,
  }));

  const todoIdMap = todos.reduce(
    (acc, todo, i) => {
      acc[todo.id] = createTodo(todo);
      acc[todo.id]!.lexidx = lexi.pack(INIT_LEX_IDX + i * DEFAULT_LEX_IDX_OFFSET, "hex");
      return acc;
    },
    {} as Record<string, Todo>,
  );

  stores.spaceStore.currentTodoStore.setTodoIdMapHard(todoIdMap);
}

async function highlightTodo(todoId: TodoId, stores: Stores) {
  await waitForSelector(`[data-id="${todoId}"]`);
  const highlightStore = stores.highlightStore;
  const currentHighlightableRect = highlightStore.currentHighlightObj?.ref.current?.getBoundingClientRect();
  const highlightableId = findTodoHighlightable(todoId, highlightStore, currentHighlightableRect);
  setTimeout(() => stores.highlightStore.setCurrentHighlightId(highlightableId), 100); // next frame, prevent zustand infinite recursion
}

/** Same behavior as Select Mode ↓ — used so the tour primary button can advance via checkReadyState without keyboard. */
function tourNavigateSelectionDown(stores: Stores) {
  const { highlightStore } = stores;
  const newCurrent = highlightStore.getHighlightObjInDirectionFromRect("down");
  if (!newCurrent?.id) return;
  highlightStore.setCurrentHighlightId(newCurrent.id);
}

/** Same behavior as move-todo-up shortcut — used so the tour primary button can advance via checkReadyState without keyboard. */
function tourMoveTodoUp(stores: Stores) {
  const { highlightStore, spaceStore } = stores;
  const todoStore = spaceStore.currentTodoStore;
  const currentTodo = todoStore.currentTodo;
  if (!currentTodo) return;
  if (highlightStore.currentHighlightObj?.kind !== "todo") return;

  const { groupKey, todoId } = highlightStore.currentHighlightObj.data;
  analytics.track("move_todo_shortcut", { direction: "up" });
  const updatedGroupKey = todoStore.moveTodo(todoId, groupKey, "up");
  if (updatedGroupKey !== groupKey) {
    setTimeout(() => {
      highlightStore.setCurrentHighlightId(todoIdToHighlightId(currentTodo.id, updatedGroupKey));
    }, 75);
  }
}

const StepVideo: FC<{ url: string }> = ({ url }) => {
  return (
    <div className="w-[calc(100% + 32px)] bg-surface-cst -mx-4 my-2">
      <video className="h-48 mx-auto" src={url} autoPlay loop></video>
    </div>
  );
};

interface StepCalloutProps extends React.HTMLAttributes<HTMLDivElement> {
  color?: "default" | "blue";
}

const StepCallout: FC<StepCalloutProps> = ({ children, className, color = "default", ...otherProps }) => {
  return (
    <div
      className={twMerge(
        classNames("p-2", {
          "bg-surface-scd text-glyph-prm": color == "default",
          "bg-surface-blue-3": color == "blue",
        }),
        className,
      )}
      {...otherProps}
    >
      {children}
    </div>
  );
};
//========================================================================
//
//                            PROVIDER HOC
//
//========================================================================

let userMyFirstTodoValue = "Todo D";

const tourSteps: CustomTourStep[] = [
  {
    selector: "a-selector-that-doesnt-exist",
    position: "center",
    padding: 0,
    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-row gap-2 items-center">
            <TodayIcon className="w-6 h-6" />
            <div className="text-lg font-bold">To-day</div>
          </div>

          <div className="flex flex-row gap-6">
            <OnboardIllustration className="w-[284px] -ml-[102px] overflow-visible" />
            <div className="flex flex-col flex-1 pt-8">
              <div className="font-bold text-2xl">Welcome!</div>
              <div className="font-semibold text-[14px] leading-5">
                Check out how To-day keeps you from drowning in your endless todo list.
              </div>
            </div>
          </div>
        </div>
      </TourStep>
    ),
    styles: {
      maskWrapper: (base) => ({
        ...base,
        backgroundColor: "blue",
      }),
    },
    async stateSetup(stores) {
      stores.spaceStore.currentTodoStore.setTodoIdMapHard({});
    },
    nextAction: {
      title: "Get Started!",
      shortcut: "enter",
      isClickable: true,
    },
  },
  {
    selector: "a-selector-that-doesnt-exist",
    position: "center",
    padding: 0,
    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div>To-day uses keyboard shortcuts! Follow the shortcuts to navigate through the tutorial.</div>
      </TourStep>
    ),
    async stateSetup(stores) {
      stores.spaceStore.currentTodoStore.setTodoIdMapHard({});
    },
    nextAction: {
      title: "Press Enter",
      shortcut: "enter",
      onTrigger: () => {
        TourEvents.emit("ON_REGISTER_SEEN");
        TourEvents.emit("NEXT_STEP");
      },
    },
  },
  {
    selector: "[data-id='todo_a']",
    highlightedSelectors: ["[data-id='todo_a']", "[data-id='todo_b']", "[data-id='todo_c']", "[data-id='todo_d']"],
    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div>
          To begin, To-day starts off in <span className="underline">Select Mode</span>. The selection moves with{" "}
          <Hotkey combo={Shortcuts.selectMode.arrows} mode="contrast" />.
          <StepVideo url="/tutorials/01_select_mode.mov" />
          Move the selection to <strong>{userMyFirstTodoValue}</strong> to continue.
        </div>
      </TourStep>
    ),
    async stateSetup(stores) {
      withTourState(stores, [
        { id: "todo_a", value: "Todo A" },
        { id: "todo_b", value: "Todo B", parent: "todo_a" },
        { id: "todo_c", value: "Todo C", parent: "todo_a" },
        { id: "todo_d", value: userMyFirstTodoValue },
      ]);

      await highlightTodo("todo_a", stores);
    },
    checkReadyState: (stores) => {
      // look for "Submit report" to be the top todo
      if (stores.highlightStore.currentHighlightObj?.kind !== "todo") return false;
      return stores.highlightStore.currentHighlightObj.data.todoId === "todo_d";
    },
    allowShortcuts: [Shortcuts.selectMode.navigateUp, Shortcuts.selectMode.navigateDown],
    nextAction: {
      title: "Select Todo D",
      shortcut: Shortcuts.selectMode.navigateDown,
      onTrigger: tourNavigateSelectionDown,
    },
  },
  {
    selector: "[data-id='todo_a']",
    highlightedSelectors: ["[data-id='todo_a']", "[data-id='todo_b']", "[data-id='todo_c']", "[data-id='todo_d']"],
    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div>
          Press <Hotkey combo={Shortcuts.selectMode.edit} mode="contrast" /> to start editing. This puts you in{" "}
          <span className="underline">Edit Mode</span>.
        </div>
      </TourStep>
    ),
    async stateSetup(stores) {
      withTourState(stores, [
        { id: "todo_a", value: "Todo A" },
        { id: "todo_b", value: "Todo B", parent: "todo_a" },
        { id: "todo_c", value: "Todo C", parent: "todo_a" },
        { id: "todo_d", value: userMyFirstTodoValue },
      ]);

      await highlightTodo("todo_d", stores);
    },
    nextAction: {
      title: "Edit Mode",
      shortcut: Shortcuts.selectMode.edit,
    },
    checkReadyState: (stores) => {
      // look for "Submit report" to be the top todo
      return stores.spaceStore.currentTodoStore.todoIdMap["todo_d"]?.value.toLowerCase().includes("my first todo");
    },
  },
  {
    selector: "[data-id='todo_a']",
    highlightedSelectors: ["[data-id='todo_a']", "[data-id='todo_b']", "[data-id='todo_c']", "[data-id='todo_d']"],
    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div>
          Rename Todo D → “My first todo”.
          <StepVideo url="/tutorials/02_modes.mov" />
          Once you are done, press <Hotkey combo={Shortcuts.esc} mode="contrast" /> to return to Select Mode
        </div>
      </TourStep>
    ),
    async stateSetup(stores) {
      userMyFirstTodoValue = "Todo D"; // reset to starting value
      withTourState(stores, [
        { id: "todo_a", value: "Todo A" },
        { id: "todo_b", value: "Todo B", parent: "todo_a" },
        { id: "todo_c", value: "Todo C", parent: "todo_a" },
        { id: "todo_d", value: userMyFirstTodoValue },
      ]);

      await highlightTodo("todo_d", stores);

      const groupKey: GroupKey = "today";
      WebEvents.emit("WEB_FOCUS_TODO", todoIdToHighlightId("todo_d", groupKey));
    },
    nextAction: {
      title: "Select Mode",
      shortcut: Shortcuts.esc,
      onTrigger: (stores) => {
        userMyFirstTodoValue = stores.spaceStore.currentTodoStore.todoIdMap["todo_d"]!.value;
        WebEvents.emit("WEB_BLUR_TODO");
        TourEvents.emit("NEXT_STEP");
      },
    },
  },
  {
    selector: "[data-id='todo_a']",
    highlightedSelectors: ["[data-id='todo_a']", "[data-id='todo_b']", "[data-id='todo_c']", "[data-id='todo_d']"],
    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div>
          Other actions often apply to the currently selected todo.
          <br />
          <br />
          For example, press <Hotkey combo={Shortcuts.anyMode.indent} mode="contrast" /> to indent a todo.
        </div>
      </TourStep>
    ),
    async stateSetup(stores) {
      withTourState(stores, [
        { id: "todo_a", value: "Todo A" },
        { id: "todo_b", value: "Todo B", parent: "todo_a" },
        { id: "todo_c", value: "Todo C", parent: "todo_a" },
        { id: "todo_d", value: userMyFirstTodoValue },
      ]);

      await highlightTodo("todo_d", stores);
    },
    nextAction: {
      title: "Indent Todo",
      shortcut: Shortcuts.anyMode.indent,
    },
  },
  {
    selector: "[data-id='todo_d']",
    highlightedSelectors: ["[data-id='todo_a']", "[data-id='todo_b']", "[data-id='todo_c']", "[data-id='todo_d']"],
    position: "right",
    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div>
          Moving a task to the top is a great way to prioritise and focus. <br /> Move todos easily with{" "}
          <Hotkey combo={Shortcuts.selectMode.moveModifier} mode="contrast" /> and{" "}
          <Hotkey combo={Shortcuts.selectMode.arrows} mode="contrast" />
          <StepVideo url="/tutorials/03_move.mov" />
          To continue, prioritise <strong>{userMyFirstTodoValue}</strong> by moving it all the way up.
        </div>
      </TourStep>
    ),
    nextAction: {
      title: "Move Up",
      shortcut: Shortcuts.selectMode.moveTodoUp,
      onTrigger: tourMoveTodoUp,
    },
    async stateSetup(stores) {
      withTourState(stores, [
        { id: "todo_a", value: "Todo A" },
        { id: "todo_b", value: "Todo B", parent: "todo_a" },
        { id: "todo_c", value: "Todo C", parent: "todo_a" },
        { id: "todo_d", value: userMyFirstTodoValue, parent: "todo_a" },
      ]);

      await highlightTodo("todo_d", stores);
    },
    checkReadyState: (stores) => {
      // look for "Submit report" to be the top todo
      const todos = stores.spaceStore.currentTodoStore.todosByGroup["today"]!;
      if (!todos?.[0]) return;
      return todos.length > 0 && todos[0].id === "todo_d";
    },
    allowShortcuts: [Shortcuts.selectMode.moveTodoUp],
  },
  {
    selector: "[data-id='todo_d']",
    highlightedSelectors: ["[data-id='todo_a']", "[data-id='todo_b']", "[data-id='todo_c']", "[data-id='todo_d']"],
    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div>
          Great! Once you{"'"}ve finished the task, press <Hotkey combo={Shortcuts.anyMode.complete} mode="contrast" />{" "}
          to complete it.
        </div>
      </TourStep>
    ),
    async stateSetup(stores) {
      withTourState(stores, [
        { id: "todo_d", value: userMyFirstTodoValue },
        { id: "todo_a", value: "Todo A" },
        { id: "todo_b", value: "Todo B", parent: "todo_a" },
        { id: "todo_c", value: "Todo C", parent: "todo_a" },
      ]);

      await highlightTodo("todo_d", stores);
    },
    nextAction: {
      title: "Complete Todo",
      shortcut: Shortcuts.anyMode.complete,
    },
  },
  {
    selector: "[data-id='todo_d']",
    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div>
          Completed todos appear here until the end of the day, after which they will be automatically cleaned up.
          <StepCallout className="text-sm mt-2">
            With To-day Pro, you can view your history of todos with{" "}
            <Hotkey combo={Shortcuts.selectMode.history} color="secondary" mode="contrast" />
          </StepCallout>
        </div>
      </TourStep>
    ),
    async stateSetup(stores) {
      withTourState(stores, [
        { id: "todo_d", value: userMyFirstTodoValue, completed: true },
        { id: "todo_a", value: "Todo A" },
        { id: "todo_b", value: "Todo B", parent: "todo_a" },
        { id: "todo_c", value: "Todo C", parent: "todo_a" },
      ]);

      await highlightTodo("todo_a", stores);
    },
    nextAction: {
      title: "Next",
      shortcut: "enter",
      isClickable: true,
    },
  },

  {
    selector: "[data-id='todo_d']",
    position: "bottom",
    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div>
          Commands are a convenient way to edit your todos. For example, <span className="text-blue-3">{"/tmr"}</span>{" "}
          will move the todo to tomorrow.
          <br />
          <br />
          Formats like <span className="text-blue-3">/25 dec</span>, <span className="text-blue-3">/in 3 days</span>,{" "}
          <span className="text-blue-3">/next wed</span>, also work.
          <StepVideo url="/tutorials/04_commands.mov" />
        </div>
      </TourStep>
    ),
    async stateSetup(stores) {
      withTourState(stores, [{ id: "todo_d", value: userMyFirstTodoValue + " /next tues" }]);
      stores.highlightStore.setCurrentHighlightId(null);
    },
    nextAction: {
      title: "Next",
      shortcut: "enter",
      isClickable: true,
    },
  },
  {
    selector: "[data-id='full_todo']",
    position: "bottom",
    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div>
          Here are some other useful commands:
          <ul>
            <li>
              • <span className="text-blue-3">/urgent</span> prevents the task from moving to your backlog at the end of
              the day.
            </li>
            <li>
              • <span className="text-blue-3">/due</span> sets a due date.
            </li>
            <li>
              • <span className="text-blue-3">/timer</span> creates a focus timer for the todo.
            </li>
          </ul>
          <br />
        </div>
      </TourStep>
    ),
    async stateSetup(stores) {
      withTourState(stores, [
        {
          id: "full_todo",
          value: "This todo has loads of stuff!",
          dueDate: moment().add(3, "days").valueOf(),
          duration: 60 * 15,
          tag: "urgent",
        },
      ]);

      stores.spaceStore.currentTodoStore.setDisplayMode("default"); // in case next step clicks "previous"
    },
    nextAction: {
      title: "Next",
      shortcut: "enter",
      isClickable: true,
    },
  },
  {
    selector: "[data-id='full_todo2']",
    highlightedSelectors: [
      "[data-testid=todo-column-title-0]",
      "[data-testid=todo-column-title-1]",
      "[data-testid=todo-column-title-2]",
      "[data-testid=todo-column-title-3]",
      "[data-testid=todo-column-title-4]",
    ],
    position: "right",
    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div>
          You can change to <span className="underline">Week View</span> with{" "}
          <Hotkey combo={Shortcuts.view.week} mode="contrast" />, which can be easier to plan with.
          <br />
          <br />
          Press <Hotkey combo={Shortcuts.view.default} mode="contrast" /> to return to{" "}
          <span className="underline">Default View</span>.
        </div>
      </TourStep>
    ),
    async stateSetup(stores) {
      withTourState(stores, [
        {
          id: "full_todo2", // rerender mask
          value: "This todo has loads of stuff!",
          dueDate: moment().add(3, "days").valueOf(),
          duration: 60 * 15,
          tag: "urgent",
        },
      ]);

      stores.spaceStore.currentTodoStore.setDisplayMode("week");
    },
    nextAction: {
      title: "Back to Default View",
      shortcut: Shortcuts.view.default,
      onTrigger(stores) {
        stores.spaceStore.currentTodoStore.setDisplayMode("default");
        setTimeout(() => {
          TourEvents.emit("NEXT_STEP");
        }, 0);
      },
    },
  },
  {
    selector: "[data-id='todo_d']",
    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div>
          Occassionally, you{"'"}ll need a place to dump more context on your todo.
          <br />
          <br />
          Press <Hotkey combo={Shortcuts.selectMode.notes} mode="contrast" /> create a note for the selected todo!
        </div>
      </TourStep>
    ),
    async stateSetup(stores) {
      withTourState(stores, [{ id: "todo_d", value: userMyFirstTodoValue }]);

      await highlightTodo("todo_d", stores);
    },
    nextAction: {
      title: "Create Note",
      shortcut: Shortcuts.selectMode.notes,
    },
  },
  {
    selector: "[data-id='note-editor']",
    position: "center",
    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div>
          Notes are the perfect place to put more information so your week looks tidy!
          <br />
          <br />
          Once you are done, press <Hotkey combo={Shortcuts.esc} mode="contrast" /> to close.
        </div>
      </TourStep>
    ),
    async stateSetup(stores) {
      withTourState(stores, [{ id: "todo_d", value: userMyFirstTodoValue, notes_id: "note_1" }]);
      await stores.notesStore.setEditingNoteTodoId("todo_d", "MOCK_USER_ID");
      await new Promise((resolve) => setTimeout(resolve, 100)); // wait for dialog to fully appear
    },
    async stateTeardown(stores) {
      await stores.notesStore.setEditingNoteTodoId(null, null);
    },
    nextAction: {
      title: "Close",
      shortcut: "esc",
    },
  },
  {
    selector: "[data-testid='spaces-list']",
    position: (props, prevRect) => {
      return [props.x, props.y - prevRect.height - 24];
    },
    padding: {},

    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div>
          To-day uses Spaces to compartmentalize your todo lists.
          <br />
          <br />
          Press <Hotkey combo={`${Shortcuts.selectMode.spaceNavigateTrigger}+up`} mode="contrast" /> to select the {'"'}
          Work
          {'"'} Space.
        </div>
      </TourStep>
    ),
    async stateSetup(stores) {
      withTourState(stores, [
        { id: "todo_d", value: "Buy Groceries" },
        { id: "todo_e", value: "Do Laundry" },
        { id: "todo_f", value: "Wash Dishes" },
        { id: "todo_g", value: "Repair Tap" },
      ]);
    },
    async stateTeardown(stores) {
      // await stores.notesStore.setEditingNoteTodoId(null, null);
    },
    nextAction: {
      title: "Switch Space",
      shortcut: `${Shortcuts.selectMode.spaceNavigateTrigger}+up`,
    },
  },
  {
    selector: "[data-testid='spaces-list']",
    position: (props, prevRect) => {
      return [props.x, props.y - prevRect.height - 24];
    },
    padding: {},

    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div>You can send todos to Spaces by dragging them onto the Space button here.</div>
        <Callout className="text-sm my-2">
          Tip: Spaces are best suited for compartmentalizing your life, whereas <strong>#hashtags</strong> are great for
          organising todos <em>within</em> a Space.
        </Callout>
      </TourStep>
    ),
    async stateSetup(stores) {
      stores.spaceStore.setCurrentSpace(mockWorkSpace.id);

      withTourState(stores, [
        { id: "todo_d", value: "Start on report", date: moment().subtract(1, "day").valueOf(), tag: "important" },
        { id: "todo_e", value: "Reply team chat", tag: "urgent" },
        { id: "todo_f", value: "Send marketing email", duration: 5 * 60 * 1000 },
        { id: "todo_g", value: "Consolidate statistics", date: null },
      ]);
    },
    async stateTeardown(stores) {
      stores.spaceStore.setCurrentSpace(mockHomeSpace.id);
    },
    nextAction: {
      title: "Next",
      shortcut: "enter",
    },
  },
  {
    selector: "a-selector-that-doesnt-exist",
    position: "center",
    padding: 0,
    content: (props) => (
      <TourStep popoverProps={props} totalStepCount={tourSteps.length}>
        <div className="flex flex-col gap-2 items-center mt-4">
          <div className="flex flex-row gap-2 items-center">
            <TodayIcon className="w-6 h-6" />
            <div className="text-lg font-bold">To-day</div>
          </div>
          <div className="font-bold text-2xl">You{"'"}re All Set!!</div>
          <div className="flex flex-row">
            <div className="flex flex-col flex-1">
              <div className="font-semibold text-[14px] leading-5 text-center">
                Join the community on our{" "}
                <a className="text-blue-3" href="https://t.me/twodayapp" target="_blank">
                  <PiTelegramLogoFill className="text-blue-3 inline" /> Telegram Channel
                </a>{" "}
                for updates and feedback! You can also request features or report bugs on the{" "}
                <a className="text-blue-3" href="https://2day.canny.io/feature-requests" target="_blank">
                  Feature Board
                </a>
                .
              </div>
            </div>
          </div>
        </div>
      </TourStep>
    ),
    async stateSetup(stores) {
      stores.spaceStore.currentTodoStore.setTodoIdMapHard({});
      ConfettiEvents.emit("ADD_CONFETTI");
    },
    nextAction: {
      title: "Let's Go!",
      shortcut: "enter",
      isClickable: true,
    },
  },
];

export function withTour(Component: React.FC) {
  return observer(function WithTour() {
    //========================================================================
    //
    //                            ACTION BUTTON
    //
    //========================================================================
    type TourActionButtonProps = TourActionProps;

    const TourActionButton: FC<TourActionButtonProps> = ({
      title,
      shortcut,
      isClickable: _isClickable,
      className,
      onClick,
      ...otherProps
    }) => {
      return (
        <Button
          trailingIcon={shortcut ? <Hotkey combo={shortcut} mode="contrast" color="secondary" /> : undefined}
          {...otherProps}
          onClick={onClick}
          className={twMerge("group", className)}
        >
          {title}
        </Button>
      );
    };

    const stores = useStores();

    return (
      <TourProvider
        steps={tourSteps}
        disableKeyboardNavigation
        disableDotsNavigation
        disableInteraction
        showDots={false}
        showCloseButton={false}
        nextButton={({ currentStep }) => {
          const nextAction = tourSteps[currentStep]?.nextAction;
          if (nextAction) {
            const { onTrigger, ...nextButtonProps } = nextAction;
            return (
              <TourActionButton
                onClick={() => {
                  if (onTrigger) {
                    onTrigger(stores);
                    return;
                  }
                  TourEvents.emit("NEXT_STEP");
                }}
                variant="solid"
                size="small"
                mode="contrast"
                {...nextButtonProps}
              />
            );
          }
          return null;
        }}
        prevButton={({ currentStep }) => {
          return (
            <>
              <TourActionButton
                title="Skip"
                onClick={() => TourEvents.emit("SKIP")}
                color="secondary"
                variant="transparent"
                size="small"
                isClickable
              />
              <div className="flex-1" />
              {currentStep === 0 ? null : (
                <TourActionButton
                  title="Previous"
                  onClick={() => TourEvents.emit("PREVIOUS_STEP")}
                  color="secondary"
                  variant="transparent"
                  size="small"
                  isClickable
                />
              )}
            </>
          );
        }}
        onClickMask={() => {}}
        styles={{
          popover: (base) => ({
            ...base,
            borderRadius: 4,
            padding: 16,
            overflow: "hidden",
            maxWidth: 400,
            ["--reactour-accent" as string]: colors["blue-3"],
            backgroundColor: "var(--color-surface-prm)",
          }),
          maskArea: (base) => ({ ...base, rx: 4 }),
          badge: (base) => ({ display: "none" }),
          controls: (base) => ({ ...base, display: "flex", justifyContent: "flex-end", gap: 4 }),
        }}
        padding={{
          mask: 24,
          popover: 28,
        }}
      >
        <TourConsumer>
          <Component />
        </TourConsumer>
      </TourProvider>
    );
  });
}

function beforeEachTourStep(stores: Stores) {
  stores.spaceStore.currentTodoStore.setDisplayMode("default");
}

const tourShortcutButtonCtx = { priority: 999, name: "TOUR_ACTION_BUTTON" };
const TourConsumer: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentStep, setCurrentStep, isOpen, setIsOpen, steps } = useTour();
  const tourSteps = steps as CustomTourStep[];
  const stores = useStores();
  const currentTourStep = tourSteps[currentStep];
  const shortcut = currentTourStep?.nextAction?.shortcut;

  useEffect(() => {
    if (!isOpen) {
      useHotkeyContext.getState().setAllowedShortcuts(undefined);
      tourSteps[currentStep]?.stateTeardown?.(stores);
      return;
    }
    useHotkeyContext.getState().setAllowedShortcuts(tourSteps[currentStep]?.allowShortcuts);
  }, [tourSteps, currentStep, isOpen, stores]);

  useEffect(() => {
    async function setStep(step: number) {
      const prevStep = currentStep;
      const teardownProm = tourSteps[prevStep]?.stateTeardown?.(stores);
      if (teardownProm) await teardownProm;
      const setupProm = tourSteps[step]?.stateSetup?.(stores);
      if (setupProm) await setupProm;

      setTimeout(() => {
        setCurrentStep(Math.max(0, Math.min(step, tourSteps.length - 1)));
      }, SETUP_DELAY); // wait for setup, then render next step
    }

    function onNext() {
      beforeEachTourStep(stores);
      if (currentStep + 1 > tourSteps.length - 1) {
        analytics.track("onboarding_completed");
        TourEvents.emit("FINISH");
        return;
      }
      setStep(currentStep + 1);
    }

    function onPrevious() {
      beforeEachTourStep(stores);
      setStep(currentStep - 1);
    }

    function onSkip() {
      analytics.track("onboarding_skipped", { step: currentStep });
      TourEvents.emit("ON_REGISTER_SEEN");
      onFinish();
    }

    async function onFinish() {
      DEBUG_START_STEP && TourEvents.emit("ON_REGISTER_SEEN"); // for debugging purposes, "register_seen" step may be skipped
      useTutorial.getState().setTutorial(false); // stop mock stores
      setIsOpen(false);
    }

    function onStart() {
      useTutorial.getState().setTutorial(true); // mock stores
      setIsOpen(true);
      beforeEachTourStep(stores);
      setStep(DEBUG_START_STEP);
    }

    TourEvents.on("NEXT_STEP", onNext);
    TourEvents.on("PREVIOUS_STEP", onPrevious);
    TourEvents.on("SKIP", onSkip);
    TourEvents.on("FINISH", onFinish);
    TourEvents.on("START", onStart);

    return () => {
      TourEvents.off("NEXT_STEP", onNext);
      TourEvents.off("PREVIOUS_STEP", onPrevious);
      TourEvents.off("SKIP", onSkip);
      TourEvents.off("FINISH", onFinish);
      TourEvents.off("START", onStart);
    };
  }, [stores, currentStep, setCurrentStep, setIsOpen, tourSteps]);

  // checkReadyState watcher
  useEffect(() => {
    function checkState() {
      const curStep = tourSteps[currentStep];
      const isReady = curStep?.checkReadyState?.(stores);
      if (isReady) {
        TourEvents.emit("NEXT_STEP");
      }
    }

    WebEvents.on("WEB_ON_HIGHLIGHT_CHANGED", checkState);
    WebEvents.on("WEB_ON_MOVED_TODO", checkState);

    return () => {
      WebEvents.off("WEB_ON_HIGHLIGHT_CHANGED", checkState);
      WebEvents.off("WEB_ON_MOVED_TODO", checkState);
    };
  }, [currentStep, stores, tourSteps]);

  useEffect(() => {
    DEBUG_START_STEP && TourEvents.emit("START");
  }, []);

  useCtxHotkeys(
    shortcut
      ? [
          {
            combo: shortcut ?? "enter",
            onKeyDown: () => {
              if (currentTourStep?.nextAction?.onTrigger) {
                currentTourStep.nextAction.onTrigger(stores);
                return;
              }
              TourEvents.emit("NEXT_STEP");
            },
            preventDefault: true,
            allowInInput: true,
          },
          {
            combo: "left",
            onKeyDown: () => {
              TourEvents.emit("PREVIOUS_STEP");
            },
            preventDefault: true,
            allowInInput: true,
          },
        ]
      : [],
    tourShortcutButtonCtx,
    Boolean(isOpen) && !tourSteps[currentStep]?.allowShortcuts /* this means we expect app level shortcuts */,
  );

  return <>{children}</>;
};
